import {
  Box,
  Checkbox,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import Image from 'src/components/image';
import Scrollbar from 'src/components/scrollbar';
import { TablePaginationCustom } from 'src/components/table';
import Iconify from 'src/components/iconify';
import { GetProfileTreatmentDto } from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';
import {
  TAB_TABLE_CONTAINER_SX,
  TAB_TABLE_PAGINATION_SX,
  TAB_TABLE_ROWS_PER_PAGE_OPTIONS,
  TAB_TABLE_SCROLL_BODY_SX,
} from 'src/theme/tableContainer';

interface ProfileTreatmentsTableProps {
  data: GetProfileTreatmentDto[];
  onRowClick: (treatmentId: string) => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectPage: (ids: string[], checked: boolean) => void;
  /**
   * Whether rows can be selected. Off inside a visit, where the table records
   * what happened rather than feeding a PDF export.
   */
  enableSelection?: boolean;
  /**
   * Paging is driven from above: the rows arrive a page at a time from the
   * server. `total` counts every treatment, not the ones in `data`.
   */
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (event: unknown, page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export default function ProfileTreatmentsTable({
  data,
  onRowClick,
  selectedIds,
  onToggleSelect,
  onToggleSelectPage,
  enableSelection = true,
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange,
}: ProfileTreatmentsTableProps) {
  // `data` is already this page: the server sliced it.
  const paginated = data;

  const pageIds = paginated.map((t) => t.id);
  // Page-scoped, both the state and the click: selecting or clearing every row
  // across pages belongs to the card header's menu. Reading this box against
  // all rows while clicking it acted on the page left a fully-selected page
  // showing unchecked, so the next click re-selected the same rows instead of
  // clearing them.
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected =
    pageIds.some((id) => selectedIds.has(id)) && !allPageSelected;

  const TableHeader = (
    <TableHead>
      <TableRow>
        {enableSelection && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={somePageSelected}
              checked={allPageSelected}
              onChange={(e) => onToggleSelectPage(pageIds, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
        )}
        <TableCell>Nazwa</TableCell>
        <TableCell>Data zabiegu</TableCell>
        <TableCell>Leki</TableCell>
        <TableCell align="center">
          {translateKey('visit.visitCourseMenuReminder')}
        </TableCell>
        <TableCell>Dodano przez</TableCell>
      </TableRow>
    </TableHead>
  );

  return (
    <>
      <TableContainer sx={TAB_TABLE_CONTAINER_SX}>
        {paginated.length > 0 ? (
          <Box sx={TAB_TABLE_SCROLL_BODY_SX}>
            <Scrollbar>
              <Table stickyHeader>
                {TableHeader}
                <TableBody>
                  {paginated.map((treatment) => (
                    <TableRow
                      key={treatment.id}
                      hover
                      data-testid="treatment-row"
                      data-treatment-id={treatment.id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => onRowClick(treatment.id)}
                      selected={enableSelection && selectedIds.has(treatment.id)}
                    >
                      {enableSelection && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(treatment.id)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => onToggleSelect(treatment.id)}
                          />
                        </TableCell>
                      )}
                      {/* The name ellipsises at 200px — "Czyszczenie gruczołów
                          okołoodbytowych" needs 303 — so the full one is only
                          reachable on hover. Same treatment as the file
                          description column. */}
                      <Tooltip
                        title={treatment.name}
                        arrow
                        disableHoverListener={
                          !treatment.name || treatment.name.length < 25
                        }
                      >
                        <TableCell
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {treatment.name}
                        </TableCell>
                      </Tooltip>
                      <TableCell>
                        {treatment.treatmentDate
                          ? format(
                              new Date(treatment.treatmentDate),
                              'dd/MM/yyyy HH:mm',
                              { locale: pl },
                            )
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          flexWrap="wrap"
                          gap={0.5}
                        >
                          {treatment.associatedMedicines?.length > 0
                            ? treatment.associatedMedicines.map((m) => (
                                <Chip
                                  key={m.medicineProductId}
                                  label={m.medicineName}
                                  size="small"
                                  variant="outlined"
                                />
                              ))
                            : '-'}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        {treatment.linkedReminder ? (
                          <Tooltip title="Przypomnienie aktywne">
                            <Box sx={{ display: 'inline-flex' }}>
                              <Iconify
                                icon="solar:check-circle-bold"
                                sx={{
                                  color: 'success.main',
                                  width: 22,
                                  height: 22,
                                }}
                              />
                            </Box>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Brak przypomnienia">
                            <Box sx={{ display: 'inline-flex' }}>
                              <Iconify
                                icon="solar:close-circle-bold"
                                sx={{
                                  color: 'error.main',
                                  width: 22,
                                  height: 22,
                                }}
                              />
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {treatment.creatorName}
                        </Typography>
                        {treatment.creatorOrganizationName && (
                          <Typography variant="caption" color="text.secondary">
                            {treatment.creatorOrganizationName}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Scrollbar>
          </Box>
        ) : (
          <>
            <Table stickyHeader>{TableHeader}</Table>
            <Stack
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              flex="1"
              gap={2}
            >
              <Image
                sx={{ width: '200px' }}
                alt="empty treatments"
                src="/assets/images/empty_table.svg"
              />
              <Typography>{translateKey('hardcoded.brak_zabiegow')}</Typography>
            </Stack>
          </>
        )}
      </TableContainer>
      <TablePaginationCustom
        count={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={TAB_TABLE_ROWS_PER_PAGE_OPTIONS}
        sx={TAB_TABLE_PAGINATION_SX}
      />
    </>
  );
}
